import type { PhotoSlotId } from "../config";
import { LIVE_MODEL_URL, LIVE_WASM_BASE } from "../config";

/** Model sınıfları: 3 slot id'si artı negatif sınıf. Sınıf adları .tflite
 * metadata'sındaki string'lerle BİREBİR aynıdır (bkz. training/README.md). */
export type LiveClass = PhotoSlotId | "other";

export interface LiveClassification {
  className: LiveClass;
  /** 0 ile 1 arası. */
  confidence: number;
}

export interface LiveClassifier {
  /** VIDEO running mode — senkron döner. timestampMs monoton artmalıdır. */
  classify(source: HTMLVideoElement | HTMLCanvasElement, timestampMs: number): LiveClassification;
  close(): void;
}

export type LiveClassifierErrorKind =
  | "model_fetch_failed"
  | "wasm_init_failed"
  | "unsupported_browser";

export class LiveClassifierError extends Error {
  readonly kind: LiveClassifierErrorKind;
  constructor(kind: LiveClassifierErrorKind, message: string) {
    super(message);
    this.name = "LiveClassifierError";
    this.kind = kind;
  }
}

const KNOWN_CLASSES: readonly LiveClass[] = [
  "front_smile",
  "upper_occlusal",
  "lower_occlusal",
];

/** Model metadata'sından gelen ham sınıf adını LiveClass'a çevirir; tanınmayan
 * ad "other" sayılır — model yeniden eğitilirse sessizce çökmek yerine güvenli
 * tarafa düşer. Test edilebilir saf fonksiyon. */
export function normalizeClassName(raw: string): LiveClass {
  return (KNOWN_CLASSES as readonly string[]).includes(raw) ? (raw as LiveClass) : "other";
}

/** Modeli yükler. Başarısızlıkta LiveClassifierError fırlatır — çağıran taraf
 * dosya-seçici akışına düşer, hata kullanıcıya sert bir duvar olarak
 * gösterilmez (spec 4.3). */
export async function loadLiveClassifier(options?: {
  modelUrl?: string;
  wasmBase?: string;
}): Promise<LiveClassifier> {
  const modelUrl = options?.modelUrl ?? LIVE_MODEL_URL;
  const wasmBase = options?.wasmBase ?? LIVE_WASM_BASE;

  if (typeof WebAssembly === "undefined") {
    throw new LiveClassifierError("unsupported_browser", "Bu tarayıcı WebAssembly desteklemiyor.");
  }

  // Model repoya girmez (npm run model:install ile kopyalanır) — eksikse
  // MediaPipe hatası "wasm_init_failed" gibi yanlış teşhis edilir, önce
  // ayrıca kontrol edilir.
  try {
    const res = await fetch(modelUrl, { method: "HEAD" });
    if (!res.ok) {
      throw new LiveClassifierError("model_fetch_failed", `Model dosyası bulunamadı: ${modelUrl} (${res.status})`);
    }
  } catch (err) {
    if (err instanceof LiveClassifierError) throw err;
    throw new LiveClassifierError(
      "model_fetch_failed",
      `Model dosyasına erişilemedi: ${modelUrl} (${err instanceof Error ? err.message : String(err)})`,
    );
  }

  // Dinamik import: Node/test ortamında (DOM/wasm yok) modül seviyesinde
  // yüklenmemesi gerekir — sanity script bunu import ederken çökmemeli.
  const { FilesetResolver, ImageClassifier } = await import("@mediapipe/tasks-vision");

  let fileset: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>;
  try {
    fileset = await FilesetResolver.forVisionTasks(wasmBase);
  } catch (err) {
    throw new LiveClassifierError(
      "wasm_init_failed",
      `MediaPipe wasm fileset yüklenemedi: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  async function createClassifier(delegate: "GPU" | "CPU") {
    return ImageClassifier.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: modelUrl, delegate },
      runningMode: "VIDEO",
      maxResults: 1,
    });
  }

  let mpClassifier;
  try {
    mpClassifier = await createClassifier("GPU");
  } catch (gpuErr) {
    try {
      mpClassifier = await createClassifier("CPU");
    } catch (err) {
      const gpuMsg = gpuErr instanceof Error ? gpuErr.message : String(gpuErr);
      const cpuMsg = err instanceof Error ? err.message : String(err);
      throw new LiveClassifierError(
        "wasm_init_failed",
        `ImageClassifier oluşturulamadı (GPU: ${gpuMsg} | CPU: ${cpuMsg})`,
      );
    }
  }

  let closed = false;

  return {
    classify(source: HTMLVideoElement | HTMLCanvasElement, timestampMs: number): LiveClassification {
      if (closed) {
        return { className: "other", confidence: 0 };
      }
      const result = mpClassifier.classifyForVideo(source, timestampMs);
      const category = result.classifications?.[0]?.categories?.[0];
      if (!category) {
        return { className: "other", confidence: 0 };
      }
      return {
        className: normalizeClassName(category.categoryName ?? ""),
        confidence: category.score ?? 0,
      };
    },
    close(): void {
      if (closed) return;
      closed = true;
      mpClassifier.close();
    },
  };
}
